// Wrapper Component for the Mantine Modal
import { ReactElement, ReactNode, useEffect, useRef } from 'react';
import { CloseButton, Modal as MantineModal } from '@mantine/core';
import styles from './Modal.module.scss';

// Link to Mantine Modal Component: https://mantine.dev/core/modal/

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

type Props = {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  classNames?: any;
  children: ReactNode;
  withCloseButton?: boolean;
  closeButtonAriaLabel?: string;
  labelledBy?: string;
  size?: string;
}

export function Modal({
  isOpen,
  onClose,
  title,
  classNames,
  children,
  withCloseButton = true,
  closeButtonAriaLabel = 'Close dialog',
  labelledBy,
  size,
}: Props): ReactElement {
  const triggerRef = useRef<Element | null>(null);

  // aria-hidden background + save/restore focus + move focus into dialog on open
  useEffect(() => {
    const appRoot = document.getElementById('platform');
    if (!appRoot) return undefined;
    if (isOpen) {
      triggerRef.current = document.activeElement;
      appRoot.setAttribute('aria-hidden', 'true');
      // Wait for Mantine to render and animate the portal before focusing
      const timer = setTimeout(() => {
        const modal = document.querySelector('[role="dialog"]');
        if (modal) {
          if (labelledBy) modal.setAttribute('aria-labelledby', labelledBy);
          const first = modal.querySelector<HTMLElement>(FOCUSABLE);
          first?.focus();
        }
      }, 50);
      return () => {
        clearTimeout(timer);
        appRoot.removeAttribute('aria-hidden');
      };
    }
    appRoot.removeAttribute('aria-hidden');
    const restoreTimer = setTimeout(() => {
      (triggerRef.current as HTMLElement)?.focus();
    }, 0);
    return () => clearTimeout(restoreTimer);
  }, [isOpen]);

  // Manual focus trap
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const modal = document.querySelector('[role="dialog"]');
      if (!modal) return;
      const focusable = Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const isInsideModal = modal.contains(document.activeElement);

      // If focus is outside the dialog entirely, pull it back in
      if (!isInsideModal) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <MantineModal
      opened={isOpen}
      onClose={onClose}
      title={title}
      classNames={classNames || ''}
      trapFocus={false}
      withCloseButton={false}
      size={size || 'md'}
      aria-labelledby={labelledBy}
    >
      {withCloseButton
        && (
        <CloseButton
          onClick={onClose}
          aria-label={closeButtonAriaLabel}
          className={styles.close_button}
        />
        )}
      {children}
    </MantineModal>
  );
}
